'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketingAttributes, MarketingTaxonomyOption, MarketingRole } from '@sizzlenz/shared';
import { MARKETING_ACTIVATION_FORMATS, MARKETING_CHANNELS, MARKETING_COLLABORATION_MODELS, MARKETING_DELIVERY_ACTIVITIES, MARKETING_OBJECTIVES, MARKETING_ROLES, MARKETING_TACTICS, hasResolvedDirectoryFacets, resolveDirectoryFacetsFromMarketingAttributes } from '@sizzlenz/shared';
import { ArrowRight, Bookmark, BookmarkCheck, ExternalLink, Goal, Handshake, Lightbulb, Radio, Store, Target, TrendingUp, Users, Wrench } from 'lucide-react';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { FollowButton } from '@/components/FollowButton';
import { LazyImage } from '@/components/LazyImage';
import { Markdown } from '@/components/Markdown';
import { Badge } from '@/components/ui/badge';
import { PostCard } from '@/components/ui/post-card';
import type { Highlight } from '@/components/content/HighlightsEditor';
import type { ApiContent } from '@/lib/api/types';
import { buildDirectoryHrefMulti } from '@/lib/directory-filters';
import { getMarketingAttributesFromContent } from '@/lib/inspiration-filters';
import { cn } from '@/lib/utils';
import React from 'react';
export interface DemandExample {
  id: string;
  brand: string;
  /** Short summary shown below the title (maps from `content.blurb`). */
  blurb?: string;
  whatTheyDid: string;
  whyItWorks: string;
  source: string;
  attributes: MarketingAttributes;
  /** Optional image URL. Falls back to a placeholder when empty. */
  imageUrl?: string;
  /**
   * Optional markdown body preview (from `content.content`). Only populated for
   * case-study records, where it renders a short excerpt of the long-form body.
   * Renders nothing when empty.
   */
  contentPreview?: string;
  /**
   * Whether the source record is a case study. Case studies lead with their
   * highlights (key metrics) above the long-form body preview; other records
   * show highlights after the preview.
   */
  isCaseStudy?: boolean;
  /**
   * Numeric content id used by the follow button. Distinct from `id` (which is
   * the slug used for navigation). Required for the follow button to render.
   */
  followObjectId?: number;
  /** Whether the current user is following this content record. */
  following?: boolean;
  /**
   * Key metrics/stats stored on `content.data.highlights`. Rendered as a
   * compact strip on the card when `showHighlights` is enabled. Empty by
   * default.
   */
  highlights?: Highlight[];
}

/** Metric shown as a colored badge in the analysis panel. */
export interface PostMetric {
  value: string;
  label: string;
  /** Tailwind border/text color class, e.g. "border-green-500 text-green-600" */
  colorClass?: string;
}

/** Analysis data for the intelligence layout. */
export interface PostAnalysis {
  /** Short performance summary, e.g. "High-performing post" */
  performanceLabel: string;
  /** Key metrics (reach %, engagement %, saves, etc.) */
  metrics: PostMetric[];
  /** Bullet points explaining why the post worked */
  whyItWorked: string[];
  /** Audience signal summary */
  audienceSignal?: string;
}

/** Controls how the image and analysis panel are arranged in the intelligence variant. */
export type ExampleCardLayout = 'side-by-side' | 'stacked';

/**
 * How a "Show Details" trigger (title, image, etc.) behaves:
 * - "link" (default) — navigates to the `/content/[id]` page via <Link>
 * - "sheet" — calls `onOpen` (e.g. to open the record in a SheetManager sheet)
 */
export type ExampleCardDetailMode = 'link' | 'sheet';

/**
 * A demand-detail facet row inside the Demand Details block. Callers can pick
 * which of these to render via `demandFacets`.
 */
export type DemandFacetKey = 'objective' | 'tactic' | 'channel' | 'collaboration' | 'activation' | 'execution';

/** All demand facet rows, in display order. Used as the default set. */
export const ALL_DEMAND_FACETS: readonly DemandFacetKey[] = ['objective', 'tactic', 'channel', 'collaboration', 'activation', 'execution'];

/**
 * A top-level content section on the card. Callers can pick which of these to
 * render via `sections`.
 * - "whyItWorks" — the "Why it works" summary/bullets
 * - "highlights" — the key-metrics strip (still gated by `showHighlights`)
 * - "contentPreview" — the long-form body excerpt (case studies)
 * - "demandDetails" — the Demand Details facet block
 * - "providers" — the "Do something similar" providers link
 */
export type ExampleCardSection = 'whyItWorks' | 'highlights' | 'contentPreview' | 'demandDetails' | 'providers';

/** All content sections. Used as the default set. */
export const ALL_EXAMPLE_CARD_SECTIONS: readonly ExampleCardSection[] = ['whyItWorks', 'highlights', 'contentPreview', 'demandDetails'];
interface ExampleCardProps {
  example: DemandExample;
  /**
   * Layout variant:
   * - "compact" (default) — single vertical card, used in carousels
   * - "intelligence" — image + analysis panel
   */
  variant?: 'compact' | 'intelligence';
  /**
   * Arrangement of image vs analysis panel for the intelligence variant:
   * - "side-by-side" (default) — image and analysis in two columns
   * - "stacked" — image on top, analysis below
   */
  layout?: ExampleCardLayout;
  /** Analysis data for the intelligence layout. */
  analysis?: PostAnalysis;
  /** Text/icon scale for the compact Demand Details block. Defaults to "md". */
  demandDetailsSize?: DemandDetailsSize;
  /**
   * How "Show Details" triggers (title, image) behave. Defaults to "link".
   * Set to "sheet" together with `onOpen` to open the record in a sheet.
   */
  detailMode?: ExampleCardDetailMode;
  /**
   * Called when a "Show Details" trigger fires while `detailMode === 'sheet'`.
   * Typically opens the content in a SheetManager sheet.
   */
  onOpen?: () => void;
  /**
   * Label for the "more details" link/button rendered at the bottom of the
   * card. Uses the same navigation behaviour as the title (link vs sheet via
   * `detailMode`). When omitted, no link is shown.
   */
  moreDetailsLabel?: React.ReactNode;
  /** Override PostCard width class for compact variant. Pass "w-full" for responsive fill. */
  widthClass?: string;
  /**
   * When true, renders a follow button on the card. Requires
   * `example.followObjectId` to be set; otherwise the button is omitted.
   * Follow state comes from `example.following`.
   */
  showFollow?: boolean;
  /**
   * When true, renders the record's highlights (key metrics) as a compact
   * strip on the card. Requires `example.highlights` to have entries; otherwise
   * nothing is shown. Defaults to false.
   */
  showHighlights?: boolean;
  /** Cap the number of highlight tiles shown when `showHighlights`. Defaults to 4. */
  maxHighlights?: number;
  /**
   * Whether each highlight tile shows its one-line supporting detail beneath
   * the value. Only applies when `showHighlights` is enabled. Defaults to true;
   * set false for a denser, value-only highlights strip.
   */
  showHighlightDetails?: boolean;
  /**
   * Which top-level content sections to render. Defaults to all of them.
   * Note: "highlights" additionally requires `showHighlights` to be enabled.
   * Example: `sections={['whyItWorks', 'demandDetails']}`.
   */
  sections?: readonly ExampleCardSection[];
  /**
   * Which Demand Details facet rows to render, in the order given. Only applies
   * when the "demandDetails" section is shown. Defaults to all facets in their
   * canonical order. Example: `demandFacets={['objective', 'tactic', 'channel']}`.
   */
  demandFacets?: readonly DemandFacetKey[];
  className?: string;
}
function findOptionLabel<TId extends string>(options: readonly (MarketingTaxonomyOption & {
  id: TId;
})[], id: TId): string {
  return options.find(option => option.id === id)?.label ?? id;
}

/** Role-specific accent styles for the demand-role pill. */
const DEMAND_ROLE_BADGE_STYLES: Record<MarketingRole, string> = {
  'brand-building': 'border-transparent bg-[#211d4f] text-white',
  'sales-activation': 'border-transparent bg-[#ff5a36] text-white',
  'retention-advocacy': 'border-transparent bg-[#159a84] text-white'
};

/**
 * The single most compelling "what role does this play" signal, using the
 * navigation label (Build / Activate / Compound demand).
 */
function DemandRoleBadge(t0) {
  const $ = _c(7);
  const {
    attributes
  } = t0;
  const role = attributes.marketingRoles[0];
  if (!role) {
    return null;
  }
  const t1 = DEMAND_ROLE_BADGE_STYLES[role];
  let t2;
  if ($[0] !== t1) {
    t2 = cn("font-black uppercase tracking-wider", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== role) {
    t3 = findOptionLabel(MARKETING_ROLES, role);
    $[2] = role;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <Badge className={t2}>{t3}</Badge>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

/**
 * Follow button for a demand example. Renders nothing unless the example
 * carries a numeric `followObjectId` (needed to target the content record).
 * Follow state is seeded from `example.following` and the button manages its
 * own optimistic toggle + auth flow internally.
 */
function ExampleFollowButton(t0) {
  const $ = _c(4);
  const {
    example,
    className
  } = t0;
  if (example.followObjectId == null) {
    return null;
  }
  const t1 = example.following ?? false;
  let t2;
  if ($[0] !== className || $[1] !== example.followObjectId || $[2] !== t1) {
    t2 = <FollowButton objectId={example.followObjectId} objectType="content" following={t1} icon={Bookmark} activeIcon={BookmarkCheck} label="Save" activeLabel="Saved" className={className} />;
    $[0] = className;
    $[1] = example.followObjectId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
type OptionList = readonly (MarketingTaxonomyOption & {
  id: string;
})[];

/** Size scale for the compact Demand Details block. */
export type DemandDetailsSize = 'sm' | 'md' | 'lg';
interface DemandDetailsSizeStyles {
  icon: string;
  label: string;
  chip: string;
}
const DEMAND_DETAILS_SIZE_STYLES: Record<DemandDetailsSize, DemandDetailsSizeStyles> = {
  sm: {
    icon: 'size-3.5',
    label: 'text-[10px]',
    chip: 'text-xs'
  },
  md: {
    icon: 'size-4',
    label: 'text-xs',
    chip: 'text-sm'
  },
  lg: {
    icon: 'size-5',
    label: 'text-sm',
    chip: 'text-base'
  }
};

/** One "label → chips" row in the compact Demand Details block. */
function DemandFacetRow(t0) {
  const $ = _c(39);
  const {
    icon,
    label,
    ids,
    options,
    styles,
    max: t1
  } = t0;
  const max = t1 === undefined ? 3 : t1;
  if (!ids.length) {
    return null;
  }
  let overflow;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== icon || $[1] !== ids || $[2] !== label || $[3] !== max || $[4] !== options || $[5] !== styles.chip || $[6] !== styles.label) {
    const shown = ids.slice(0, max);
    overflow = ids.length - shown.length;
    t6 = "flex items-start gap-2";
    if ($[14] !== icon) {
      t7 = <span className="mt-0.5 shrink-0 text-gray-400">{icon}</span>;
      $[14] = icon;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    t4 = "flex flex-col gap-1";
    let t8;
    if ($[16] !== styles.label) {
      t8 = cn("font-black uppercase tracking-wider text-gray-400", styles.label);
      $[16] = styles.label;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    if ($[18] !== label || $[19] !== t8) {
      t5 = <span className={t8}>{label}</span>;
      $[18] = label;
      $[19] = t8;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    t2 = "flex flex-wrap gap-1";
    let t9;
    if ($[21] !== options || $[22] !== styles.chip) {
      t9 = id => <Badge key={id} variant="secondary" className={cn("font-semibold text-gray-600", styles.chip)}>{findOptionLabel(options, id)}</Badge>;
      $[21] = options;
      $[22] = styles.chip;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    t3 = shown.map(t9);
    $[0] = icon;
    $[1] = ids;
    $[2] = label;
    $[3] = max;
    $[4] = options;
    $[5] = styles.chip;
    $[6] = styles.label;
    $[7] = overflow;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    overflow = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
  }
  let t8;
  if ($[24] !== overflow || $[25] !== styles.chip) {
    t8 = overflow > 0 && <Badge variant="secondary" className={cn("font-semibold text-gray-500", styles.chip)}>+{overflow}</Badge>;
    $[24] = overflow;
    $[25] = styles.chip;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t2 || $[28] !== t3 || $[29] !== t8) {
    t9 = <div className={t2}>{t3}{t8}</div>;
    $[27] = t2;
    $[28] = t3;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t4 || $[32] !== t5 || $[33] !== t9) {
    t10 = <div className={t4}>{t5}{t9}</div>;
    $[31] = t4;
    $[32] = t5;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t6 || $[37] !== t7) {
    t11 = <div className={t6}>{t7}{t10}</div>;
    $[35] = t10;
    $[36] = t6;
    $[37] = t7;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  return t11;
}

/**
 * Condensed version of the Demand Details shown on ContentDetailView. Shows
 * only the facets that carry a compelling teaser signal — objective, tactic,
 * channel, collaboration model, activation format and execution activity — and
 * only when the record specifies values for them. The demand role is shown
 * separately as a badge, so it's omitted here. Renders nothing when no facet
 * has values.
 */
function CompactDemandDetails(t0) {
  const $ = _c(65);
  const {
    attributes,
    size: t1,
    facets: t2
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const facets = t2 === undefined ? ALL_DEMAND_FACETS : t2;
  const styles = DEMAND_DETAILS_SIZE_STYLES[size];
  let t3;
  if ($[0] !== styles.icon) {
    t3 = <Goal className={styles.icon} aria-hidden="true" />;
    $[0] = styles.icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== attributes.marketingObjectives) {
    t4 = attributes.marketingObjectives ?? [];
    $[2] = attributes.marketingObjectives;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = {
      icon: t3,
      label: "Objective",
      ids: t4,
      options: MARKETING_OBJECTIVES
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== styles.icon) {
    t6 = <Lightbulb className={styles.icon} aria-hidden="true" />;
    $[7] = styles.icon;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== attributes.tactics) {
    t7 = attributes.tactics ?? [];
    $[9] = attributes.tactics;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = {
      icon: t6,
      label: "Tactic",
      ids: t7,
      options: MARKETING_TACTICS
    };
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== styles.icon) {
    t9 = <Radio className={styles.icon} aria-hidden="true" />;
    $[14] = styles.icon;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== attributes.channels) {
    t10 = attributes.channels ?? [];
    $[16] = attributes.channels;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = {
      icon: t9,
      label: "Channel",
      ids: t10,
      options: MARKETING_CHANNELS
    };
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== styles.icon) {
    t12 = <Handshake className={styles.icon} aria-hidden="true" />;
    $[21] = styles.icon;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== attributes.collaborationModels) {
    t13 = attributes.collaborationModels ?? [];
    $[23] = attributes.collaborationModels;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t12 || $[26] !== t13) {
    t14 = {
      icon: t12,
      label: "Collaboration model",
      ids: t13,
      options: MARKETING_COLLABORATION_MODELS
    };
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== styles.icon) {
    t15 = <Store className={styles.icon} aria-hidden="true" />;
    $[28] = styles.icon;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== attributes.activationFormats) {
    t16 = attributes.activationFormats ?? [];
    $[30] = attributes.activationFormats;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t15 || $[33] !== t16) {
    t17 = {
      icon: t15,
      label: "Activation format",
      ids: t16,
      options: MARKETING_ACTIVATION_FORMATS
    };
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== styles.icon) {
    t18 = <Wrench className={styles.icon} aria-hidden="true" />;
    $[35] = styles.icon;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== attributes.deliveryActivities) {
    t19 = attributes.deliveryActivities ?? [];
    $[37] = attributes.deliveryActivities;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t18 || $[40] !== t19) {
    t20 = {
      icon: t18,
      label: "Execution activity",
      ids: t19,
      options: MARKETING_DELIVERY_ACTIVITIES
    };
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t11 || $[43] !== t14 || $[44] !== t17 || $[45] !== t20 || $[46] !== t5 || $[47] !== t8) {
    t21 = {
      objective: t5,
      tactic: t8,
      channel: t11,
      collaboration: t14,
      activation: t17,
      execution: t20
    };
    $[42] = t11;
    $[43] = t14;
    $[44] = t17;
    $[45] = t20;
    $[46] = t5;
    $[47] = t8;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const facetConfig = t21;
  let t22;
  let t23;
  let t24;
  let t25;
  if ($[49] !== facetConfig || $[50] !== facets || $[51] !== styles) {
    t25 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t26;
      if ($[56] !== facetConfig) {
        t26 = key => facetConfig[key].ids.length > 0;
        $[56] = facetConfig;
        $[57] = t26;
      } else {
        t26 = $[57];
      }
      const selected = facets.filter(t26);
      if (selected.length === 0) {
        t25 = null;
        break bb0;
      }
      t22 = "flex flex-col gap-2.5";
      t23 = "Demand details";
      let t27;
      if ($[58] !== facetConfig || $[59] !== styles) {
        t27 = key_0 => {
          const config = facetConfig[key_0];
          return <DemandFacetRow key={key_0} icon={config.icon} label={config.label} ids={config.ids} options={config.options} styles={styles} />;
        };
        $[58] = facetConfig;
        $[59] = styles;
        $[60] = t27;
      } else {
        t27 = $[60];
      }
      t24 = selected.map(t27);
    }
    $[49] = facetConfig;
    $[50] = facets;
    $[51] = styles;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
    $[55] = t25;
  } else {
    t22 = $[52];
    t23 = $[53];
    t24 = $[54];
    t25 = $[55];
  }
  if (t25 !== Symbol.for("react.early_return_sentinel")) {
    return t25;
  }
  let t26;
  if ($[61] !== t22 || $[62] !== t23 || $[63] !== t24) {
    t26 = <div className={t22} aria-label={t23}>{t24}</div>;
    $[61] = t22;
    $[62] = t23;
    $[63] = t24;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  return t26;
}

/**
 * Compact, card-friendly rendering of a record's highlights (the same
 * `{ title, value, details }` metrics shown large on ContentDetailView). Kept
 * deliberately dense: each highlight is a tile with a small label, a bold
 * value, and optional one-line supporting detail. Laid out as a responsive
 * two-column grid so a pair of metrics sits side-by-side, matching the detail
 * view's paired presentation but scaled down for the feed card.
 *
 * Renders nothing when there are no highlights. The `max` prop caps how many
 * tiles show so the card stays compact; any remainder is summarised as a
 * trailing "+N more" note.
 */
function CompactHighlights(t0) {
  const $ = _c(19);
  const {
    highlights,
    max: t1,
    showDetails: t2
  } = t0;
  const max = t1 === undefined ? 4 : t1;
  const showDetails = t2 === undefined ? true : t2;
  let overflow;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== highlights || $[1] !== max || $[2] !== showDetails) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const withValue = highlights.filter(_temp);
      if (withValue.length === 0) {
        t6 = null;
        break bb0;
      }
      const shown = withValue.slice(0, max);
      overflow = withValue.length - shown.length;
      t5 = "Highlights";
      t3 = "grid grid-cols-2 gap-2.5";
      let t7;
      if ($[8] !== showDetails) {
        t7 = highlight => <div key={highlight.id} className="relative overflow-hidden rounded-xl border border-[#ff5a36]/25 bg-gradient-to-br from-[#ff5a36]/[0.10] via-[#ff5a36]/[0.04] to-[#211d4f]/[0.06] px-3.5 py-3 shadow-sm"><span aria-hidden="true" className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-[#ff5a36] to-[#ff8a5c]" />{highlight.title && <p className="text-xs font-black uppercase tracking-wider text-[#ff5a36]">{highlight.title}</p>}<p className="mt-0.5 text-2xl font-black leading-none tracking-tight text-[#211d4f]">{highlight.value || "\u2014"}</p>{showDetails && highlight.details && <p className="mt-1 line-clamp-2 text-xs leading-snug text-gray-600">{highlight.details}</p>}</div>;
        $[8] = showDetails;
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      t4 = shown.map(t7);
    }
    $[0] = highlights;
    $[1] = max;
    $[2] = showDetails;
    $[3] = overflow;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    overflow = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t4) {
    t7 = <div className={t3}>{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== overflow) {
    t8 = overflow > 0 && <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">+{overflow} more</p>;
    $[13] = overflow;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t5 || $[16] !== t7 || $[17] !== t8) {
    t9 = <div aria-label={t5}>{t7}{t8}</div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}

/**
 * "Show providers" link that resolves a record's demand attributes (tactics,
 * delivery activities, channels) to directory capability + channel-expertise
 * facets and links to a pre-filtered /directory view. Renders nothing when no
 * attribute resolves to a directory facet.
 */
function _temp(h) {
  return h.value?.trim() || h.title?.trim();
}
function ShowProvidersLink(t0) {
  const $ = _c(6);
  const {
    attributes
  } = t0;
  let t1;
  let t2;
  if ($[0] !== attributes) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const facets = resolveDirectoryFacetsFromMarketingAttributes(attributes);
      if (!hasResolvedDirectoryFacets(facets)) {
        t2 = null;
        break bb0;
      }
      t1 = buildDirectoryHrefMulti({
        capability: facets.capabilities,
        channelExpertise: facets.channelExpertise
      });
    }
    $[0] = attributes;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const href = t1;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Users className="size-3.5" aria-hidden="true" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== href) {
    t4 = <Link href={href} className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-black uppercase tracking-wider text-[#211d4f] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#211d4f]">{t3}Do something similar</Link>;
    $[4] = href;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}

/**
 * Wraps any content as a "Show Details" trigger. Renders a <Link> to the
 * content page when `mode === 'link'`, or a <button> that calls `onOpen` when
 * `mode === 'sheet'`. Use it for the title, image, or any other element that
 * should open the detail view.
 */
function ShowDetailsTrigger(t0) {
  const $ = _c(10);
  const {
    mode,
    contentId,
    onOpen,
    className,
    ariaLabel,
    children
  } = t0;
  if (mode === "sheet") {
    let t1;
    if ($[0] !== ariaLabel || $[1] !== children || $[2] !== className || $[3] !== onOpen) {
      t1 = <button type="button" onClick={onOpen} aria-label={ariaLabel} className={className}>{children}</button>;
      $[0] = ariaLabel;
      $[1] = children;
      $[2] = className;
      $[3] = onOpen;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  const t1 = `/content/${contentId}`;
  let t2;
  if ($[5] !== ariaLabel || $[6] !== children || $[7] !== className || $[8] !== t1) {
    t2 = <Link href={t1} aria-label={ariaLabel} className={className}>{children}</Link>;
    $[5] = ariaLabel;
    $[6] = children;
    $[7] = className;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}

/**
 * "More details" affordance shown at the bottom of the card. Uses the same
 * link-vs-sheet behaviour as the title (via ShowDetailsTrigger) so it navigates
 * to `/content/[id]` or opens the record in a sheet consistently. Renders
 * nothing when no label is provided.
 */
function MoreDetailsLink(t0) {
  const $ = _c(5);
  const {
    label,
    mode,
    contentId,
    onOpen
  } = t0;
  if (!label) {
    return null;
  }
  let t1;
  if ($[0] !== contentId || $[1] !== label || $[2] !== mode || $[3] !== onOpen) {
    t1 = <ShowDetailsTrigger mode={mode} contentId={contentId} onOpen={onOpen} className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-black uppercase tracking-wider text-[#211d4f] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#211d4f]">{label}</ShowDetailsTrigger>;
    $[0] = contentId;
    $[1] = label;
    $[2] = mode;
    $[3] = onOpen;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

/**
 * Bodies at or below this length render in full — no truncation.
 * Short case studies read better whole than with an arbitrary cut-off.
 */
const CONTENT_PREVIEW_MIN_CHARS = 240;
/**
 * Generous ceiling on excerpt length. This bounds the DOM size only — the
 * actual visible amount is decided by the available column height via CSS
 * overflow, so we render more than fits and let the layout clip it. Sized to
 * comfortably fill a tall side-by-side column next to the image.
 */
const CONTENT_PREVIEW_MAX_CHARS = 1200;
/**
 * Once past the min length, we look for a clean break (paragraph, then
 * sentence, then word) no earlier than this fraction of the max, so the excerpt
 * doesn't collapse to a tiny fragment when an early break exists.
 */
const CONTENT_PREVIEW_MIN_BREAK_RATIO = 0.5;
interface PreviewExcerpt {
  text: string;
  truncated: boolean;
}

/**
 * Build an excerpt sized to the content:
 * - Short bodies pass through untouched.
 * - Longer bodies are cut at the cleanest available boundary within the window
 *   (end of the first paragraph, else a sentence end, else a word boundary),
 *   preferring more text over a very early break.
 */
function buildPreviewExcerpt(content: string): PreviewExcerpt {
  const trimmed = content.trim();
  if (trimmed.length <= CONTENT_PREVIEW_MIN_CHARS) {
    return {
      text: trimmed,
      truncated: false
    };
  }
  const window = trimmed.slice(0, CONTENT_PREVIEW_MAX_CHARS);
  const minBreak = Math.floor(CONTENT_PREVIEW_MAX_CHARS * CONTENT_PREVIEW_MIN_BREAK_RATIO);

  // Prefer the end of the first block (blank line) so we don't slice through a
  // markdown structure like a list or heading.
  const paragraphBreak = window.indexOf('\n\n');
  if (paragraphBreak >= minBreak) {
    return {
      text: window.slice(0, paragraphBreak).trimEnd(),
      truncated: true
    };
  }

  // Else cut at the last sentence end within the window.
  const sentenceEnd = Math.max(window.lastIndexOf('. '), window.lastIndexOf('! '), window.lastIndexOf('? '));
  if (sentenceEnd >= minBreak) {
    return {
      text: window.slice(0, sentenceEnd + 1).trimEnd(),
      truncated: true
    };
  }

  // Else fall back to the last word boundary, appending an ellipsis.
  const wordBreak = window.lastIndexOf(' ');
  const cut = wordBreak >= minBreak ? wordBreak : CONTENT_PREVIEW_MAX_CHARS;
  return {
    text: `${window.slice(0, cut).trimEnd()}…`,
    truncated: true
  };
}

/**
 * Short markdown-body excerpt for records that carry long-form content (e.g.
 * case studies). Sizes the excerpt to the content: short bodies render whole,
 * longer bodies are trimmed at a clean boundary and clamped so they read as a
 * teaser rather than the full article. Returns nothing when the content is
 * empty.
 */
function ContentPreview(t0) {
  const $ = _c(8);
  const {
    content
  } = t0;
  let t1;
  let t2;
  if ($[0] !== content) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const trimmed = content?.trim();
      if (!trimmed) {
        t2 = null;
        break bb0;
      }
      t1 = buildPreviewExcerpt(trimmed);
    }
    $[0] = content;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const {
    text
  } = t1;
  let t3;
  if ($[3] !== text) {
    t3 = <Markdown expandableImages={false} className="text-sm leading-relaxed text-gray-600 [&_*]:!my-0 [&_*]:text-sm [&_p]:text-gray-600">{text}</Markdown>;
    $[3] = text;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-linear-to-t from-white to-transparent" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <div className="relative max-h-40 overflow-hidden sm:max-h-60">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function BrandPlaceholder(t0) {
  const $ = _c(3);
  const {
    brand
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-gray-200"><TrendingUp className="size-7 text-gray-400" aria-hidden="true" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== brand) {
    t2 = <div className="flex size-full items-center justify-center bg-gray-100"><div className="text-center">{t1}<p className="mt-2 text-sm font-bold text-gray-500">{brand}</p></div></div>;
    $[1] = brand;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function ExampleCard(t0) {
  const $ = _c(85);
  const {
    example,
    variant: t1,
    layout: t2,
    analysis,
    demandDetailsSize: t3,
    detailMode: t4,
    onOpen,
    moreDetailsLabel,
    widthClass,
    showFollow: t5,
    showHighlights: t6,
    maxHighlights: t7,
    showHighlightDetails: t8,
    sections: t9,
    demandFacets: t10,
    className
  } = t0;
  const variant = t1 === undefined ? "compact" : t1;
  const layout = t2 === undefined ? "side-by-side" : t2;
  const demandDetailsSize = t3 === undefined ? "md" : t3;
  const detailMode = t4 === undefined ? "link" : t4;
  const showFollow = t5 === undefined ? false : t5;
  const showHighlights = t6 === undefined ? false : t6;
  const maxHighlights = t7 === undefined ? 4 : t7;
  const showHighlightDetails = t8 === undefined ? true : t8;
  const sections = t9 === undefined ? ALL_EXAMPLE_CARD_SECTIONS : t9;
  const demandFacets = t10 === undefined ? ALL_DEMAND_FACETS : t10;
  let t11;
  if ($[0] !== sections) {
    t11 = section => sections.includes(section);
    $[0] = sections;
    $[1] = t11;
  } else {
    t11 = $[1];
  }
  const showSection = t11;
  if (variant === "intelligence") {
    let t12;
    if ($[2] !== analysis || $[3] !== className || $[4] !== demandDetailsSize || $[5] !== demandFacets || $[6] !== detailMode || $[7] !== example || $[8] !== layout || $[9] !== maxHighlights || $[10] !== moreDetailsLabel || $[11] !== onOpen || $[12] !== sections || $[13] !== showFollow || $[14] !== showHighlightDetails || $[15] !== showHighlights) {
      t12 = <IntelligenceLayout example={example} layout={layout} analysis={analysis} demandDetailsSize={demandDetailsSize} detailMode={detailMode} onOpen={onOpen} moreDetailsLabel={moreDetailsLabel} showFollow={showFollow} showHighlights={showHighlights} maxHighlights={maxHighlights} showHighlightDetails={showHighlightDetails} sections={sections} demandFacets={demandFacets} className={className} />;
      $[2] = analysis;
      $[3] = className;
      $[4] = demandDetailsSize;
      $[5] = demandFacets;
      $[6] = detailMode;
      $[7] = example;
      $[8] = layout;
      $[9] = maxHighlights;
      $[10] = moreDetailsLabel;
      $[11] = onOpen;
      $[12] = sections;
      $[13] = showFollow;
      $[14] = showHighlightDetails;
      $[15] = showHighlights;
      $[16] = t12;
    } else {
      t12 = $[16];
    }
    return t12;
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex size-8 items-center justify-center rounded-full bg-gray-100"><TrendingUp className="size-4 text-gray-500" aria-hidden="true" /></div>;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== detailMode || $[19] !== example.brand || $[20] !== example.id || $[21] !== onOpen) {
    t13 = <ShowDetailsTrigger mode={detailMode} contentId={example.id} onOpen={onOpen} className="cursor-pointer text-left text-sm font-bold text-gray-900 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#211d4f]">{example.brand}</ShowDetailsTrigger>;
    $[18] = detailMode;
    $[19] = example.brand;
    $[20] = example.id;
    $[21] = onOpen;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ExternalLink className="size-3" aria-hidden="true" />;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== example.source) {
    t15 = <span className="inline-flex items-center gap-1 text-xs text-gray-500">{t14}{example.source}</span>;
    $[24] = example.source;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== example || $[27] !== showFollow) {
    t16 = showFollow && <ExampleFollowButton example={example} className="px-2 py-1" />;
    $[26] = example;
    $[27] = showFollow;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t15 || $[30] !== t16) {
    t17 = <div className="flex items-center gap-2">{t15}{t16}</div>;
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t13 || $[33] !== t17) {
    t18 = <>{t12}<div className="flex flex-1 items-center justify-between gap-2">{t13}{t17}</div></>;
    $[32] = t13;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = `View ${example.brand} details`;
  const t20 = `${example.brand} demand example`;
  let t21;
  if ($[35] !== example.brand) {
    t21 = <BrandPlaceholder brand={example.brand} />;
    $[35] = example.brand;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== example.imageUrl || $[38] !== t20 || $[39] !== t21) {
    t22 = <LazyImage src={example.imageUrl} alt={t20} className="absolute inset-0 size-full transition-transform group-hover/media:scale-105" fallback={t21} />;
    $[37] = example.imageUrl;
    $[38] = t20;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== detailMode || $[42] !== example.id || $[43] !== onOpen || $[44] !== t19 || $[45] !== t22) {
    t23 = <ShowDetailsTrigger mode={detailMode} contentId={example.id} onOpen={onOpen} ariaLabel={t19} className="group/media absolute inset-0 block size-full cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#211d4f]">{t22}</ShowDetailsTrigger>;
    $[41] = detailMode;
    $[42] = example.id;
    $[43] = onOpen;
    $[44] = t19;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== example.whyItWorks || $[48] !== showSection) {
    t24 = showSection("whyItWorks") && <div><div className="flex items-center gap-1.5"><Lightbulb className="size-3 shrink-0 text-[#159a84]" aria-hidden="true" /><p className="text-[10px] font-black uppercase tracking-wider text-[#159a84]">Why it works</p></div><p className="mt-1 text-sm leading-relaxed text-gray-600">{example.whyItWorks}</p></div>;
    $[47] = example.whyItWorks;
    $[48] = showSection;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== example.highlights || $[51] !== maxHighlights || $[52] !== showHighlightDetails || $[53] !== showHighlights || $[54] !== showSection) {
    t25 = showSection("highlights") && showHighlights && example.highlights && <CompactHighlights highlights={example.highlights} max={maxHighlights} showDetails={showHighlightDetails} />;
    $[50] = example.highlights;
    $[51] = maxHighlights;
    $[52] = showHighlightDetails;
    $[53] = showHighlights;
    $[54] = showSection;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== example.contentPreview || $[57] !== showSection) {
    t26 = showSection("contentPreview") && <ContentPreview content={example.contentPreview} />;
    $[56] = example.contentPreview;
    $[57] = showSection;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== demandDetailsSize || $[60] !== demandFacets || $[61] !== example.attributes || $[62] !== showSection) {
    t27 = showSection("demandDetails") && <CompactDemandDetails attributes={example.attributes} size={demandDetailsSize} facets={demandFacets} />;
    $[59] = demandDetailsSize;
    $[60] = demandFacets;
    $[61] = example.attributes;
    $[62] = showSection;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== example.attributes || $[65] !== showSection) {
    t28 = showSection("providers") && <ShowProvidersLink attributes={example.attributes} />;
    $[64] = example.attributes;
    $[65] = showSection;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== detailMode || $[68] !== example.id || $[69] !== moreDetailsLabel || $[70] !== onOpen) {
    t29 = moreDetailsLabel && <div className="flex justify-end border-t border-gray-100 pt-3"><MoreDetailsLink label={moreDetailsLabel} mode={detailMode} contentId={example.id} onOpen={onOpen} /></div>;
    $[67] = detailMode;
    $[68] = example.id;
    $[69] = moreDetailsLabel;
    $[70] = onOpen;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== t24 || $[73] !== t25 || $[74] !== t26 || $[75] !== t27 || $[76] !== t28 || $[77] !== t29) {
    t30 = <div className="flex flex-col gap-3">{t24}{t25}{t26}{t27}{t28}{t29}</div>;
    $[72] = t24;
    $[73] = t25;
    $[74] = t26;
    $[75] = t27;
    $[76] = t28;
    $[77] = t29;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  let t31;
  if ($[79] !== className || $[80] !== t18 || $[81] !== t23 || $[82] !== t30 || $[83] !== widthClass) {
    t31 = <PostCard className={className} widthClass={widthClass} header={t18} media={t23} body={t30} />;
    $[79] = className;
    $[80] = t18;
    $[81] = t23;
    $[82] = t30;
    $[83] = widthClass;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  return t31;
}
function IntelligenceLayout(t0) {
  const $ = _c(92);
  const {
    example,
    layout: t1,
    analysis,
    demandDetailsSize: t2,
    detailMode: t3,
    onOpen,
    moreDetailsLabel,
    showFollow: t4,
    showHighlights: t5,
    maxHighlights: t6,
    showHighlightDetails: t7,
    sections: t8,
    demandFacets: t9,
    className
  } = t0;
  const layout = t1 === undefined ? "side-by-side" : t1;
  const demandDetailsSize = t2 === undefined ? "md" : t2;
  const detailMode = t3 === undefined ? "link" : t3;
  const showFollow = t4 === undefined ? false : t4;
  const showHighlights = t5 === undefined ? false : t5;
  const maxHighlights = t6 === undefined ? 4 : t6;
  const showHighlightDetails = t7 === undefined ? true : t7;
  const sections = t8 === undefined ? ALL_EXAMPLE_CARD_SECTIONS : t8;
  const demandFacets = t9 === undefined ? ALL_DEMAND_FACETS : t9;
  const isSideBySide = layout === "side-by-side";
  let t10;
  if ($[0] !== sections) {
    t10 = section => sections.includes(section);
    $[0] = sections;
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const showSection = t10;
  let t11;
  if ($[2] !== className || $[3] !== isSideBySide) {
    t11 = isSideBySide && !className?.includes("w-full") && "sm:w-150";
    $[2] = className;
    $[3] = isSideBySide;
    $[4] = t11;
  } else {
    t11 = $[4];
  }
  let t12;
  if ($[5] !== className || $[6] !== t11) {
    t12 = cn("flex w-[320px] shrink-0 flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md", t11, className);
    $[5] = className;
    $[6] = t11;
    $[7] = t12;
  } else {
    t12 = $[7];
  }
  let t13;
  if ($[8] !== detailMode || $[9] !== example.brand || $[10] !== example.id || $[11] !== onOpen) {
    t13 = <ShowDetailsTrigger mode={detailMode} contentId={example.id} onOpen={onOpen} className="cursor-pointer text-left text-lg font-black leading-snug text-gray-900 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#211d4f]">{example.brand}</ShowDetailsTrigger>;
    $[8] = detailMode;
    $[9] = example.brand;
    $[10] = example.id;
    $[11] = onOpen;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] !== example || $[14] !== showFollow) {
    t14 = showFollow && <ExampleFollowButton example={example} className="shrink-0" />;
    $[13] = example;
    $[14] = showFollow;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t13 || $[17] !== t14) {
    t15 = <div className="flex items-start justify-between gap-3">{t13}{t14}</div>;
    $[16] = t13;
    $[17] = t14;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== example.blurb) {
    t16 = example.blurb && <p className="text-sm leading-relaxed text-gray-600">{example.blurb}</p>;
    $[19] = example.blurb;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== t15 || $[22] !== t16) {
    t17 = <div className="flex flex-col gap-2">{t15}{t16}</div>;
    $[21] = t15;
    $[22] = t16;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  const t18 = isSideBySide && "sm:grid-cols-[1fr_1fr] sm:items-start";
  let t19;
  if ($[24] !== t18) {
    t19 = cn("grid grid-cols-1 gap-4", t18);
    $[24] = t18;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  const t20 = example.id;
  const t21 = `View ${example.brand} details`;
  const t22 = example.imageUrl;
  const t23 = `${example.brand} post`;
  let t24;
  if ($[26] !== example.whatTheyDid) {
    t24 = example.whatTheyDid.split(" ").slice(0, 6).join(" ");
    $[26] = example.whatTheyDid;
    $[27] = t24;
  } else {
    t24 = $[27];
  }
  let t25;
  if ($[28] !== t24) {
    t25 = <div className="flex size-full items-center justify-center p-6"><p className="text-center text-lg font-black uppercase leading-tight text-gray-800">{t24}</p></div>;
    $[28] = t24;
    $[29] = t25;
  } else {
    t25 = $[29];
  }
  let t26;
  if ($[30] !== example.imageUrl || $[31] !== t23 || $[32] !== t25) {
    t26 = <LazyImage src={t22} alt={t23} className="absolute inset-0 size-full transition-transform group-hover/media:scale-105" fallback={t25} />;
    $[30] = example.imageUrl;
    $[31] = t23;
    $[32] = t25;
    $[33] = t26;
  } else {
    t26 = $[33];
  }
  let t27;
  if ($[34] !== detailMode || $[35] !== example.id || $[36] !== onOpen || $[37] !== t21 || $[38] !== t26) {
    t27 = <ShowDetailsTrigger mode={detailMode} contentId={t20} onOpen={onOpen} ariaLabel={t21} className="group/media relative block aspect-4/3 w-full cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-linear-to-br from-gray-50 to-gray-100 outline-none focus-visible:ring-2 focus-visible:ring-[#211d4f]">{t26}</ShowDetailsTrigger>;
    $[34] = detailMode;
    $[35] = example.id;
    $[36] = onOpen;
    $[37] = t21;
    $[38] = t26;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== analysis || $[41] !== example.whyItWorks || $[42] !== showSection) {
    t28 = showSection("whyItWorks") && (analysis?.whyItWorked.length ? <div><div className="flex items-center gap-1.5"><Lightbulb className="size-3 shrink-0 text-[#159a84]" aria-hidden="true" /><p className="text-[10px] font-black uppercase tracking-wider text-[#159a84]">Why it works</p></div><ul className="mt-1.5 flex flex-col gap-1">{analysis.whyItWorked.map(_temp2)}</ul></div> : example.whyItWorks && <div><div className="flex items-center gap-1.5"><Lightbulb className="size-3 shrink-0 text-[#159a84]" aria-hidden="true" /><p className="text-[10px] font-black uppercase tracking-wider text-[#159a84]">Why it works</p></div><p className="mt-1 text-sm leading-relaxed text-gray-600">{example.whyItWorks}</p></div>);
    $[40] = analysis;
    $[41] = example.whyItWorks;
    $[42] = showSection;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] !== example.highlights || $[45] !== example.isCaseStudy || $[46] !== maxHighlights || $[47] !== showHighlightDetails || $[48] !== showHighlights || $[49] !== showSection) {
    t29 = showSection("highlights") && example.isCaseStudy && showHighlights && example.highlights && <CompactHighlights highlights={example.highlights} max={maxHighlights} showDetails={showHighlightDetails} />;
    $[44] = example.highlights;
    $[45] = example.isCaseStudy;
    $[46] = maxHighlights;
    $[47] = showHighlightDetails;
    $[48] = showHighlights;
    $[49] = showSection;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== example.contentPreview || $[52] !== showSection) {
    t30 = showSection("contentPreview") && <ContentPreview content={example.contentPreview} />;
    $[51] = example.contentPreview;
    $[52] = showSection;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] !== example.highlights || $[55] !== example.isCaseStudy || $[56] !== maxHighlights || $[57] !== showHighlightDetails || $[58] !== showHighlights || $[59] !== showSection) {
    t31 = showSection("highlights") && !example.isCaseStudy && showHighlights && example.highlights && <CompactHighlights highlights={example.highlights} max={maxHighlights} showDetails={showHighlightDetails} />;
    $[54] = example.highlights;
    $[55] = example.isCaseStudy;
    $[56] = maxHighlights;
    $[57] = showHighlightDetails;
    $[58] = showHighlights;
    $[59] = showSection;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  if ($[61] !== demandDetailsSize || $[62] !== demandFacets || $[63] !== example.attributes || $[64] !== showSection) {
    t32 = showSection("demandDetails") && <CompactDemandDetails attributes={example.attributes} size={demandDetailsSize} facets={demandFacets} />;
    $[61] = demandDetailsSize;
    $[62] = demandFacets;
    $[63] = example.attributes;
    $[64] = showSection;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  let t33;
  if ($[66] !== example.attributes || $[67] !== showSection) {
    t33 = showSection("providers") && <ShowProvidersLink attributes={example.attributes} />;
    $[66] = example.attributes;
    $[67] = showSection;
    $[68] = t33;
  } else {
    t33 = $[68];
  }
  let t34;
  if ($[69] !== t32 || $[70] !== t33) {
    t34 = <div className="mt-auto flex flex-col gap-2 pt-1">{t32}{t33}</div>;
    $[69] = t32;
    $[70] = t33;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  let t35;
  if ($[72] !== t28 || $[73] !== t29 || $[74] !== t30 || $[75] !== t31 || $[76] !== t34) {
    t35 = <div className="flex min-h-0 flex-col gap-3 overflow-hidden">{t28}{t29}{t30}{t31}{t34}</div>;
    $[72] = t28;
    $[73] = t29;
    $[74] = t30;
    $[75] = t31;
    $[76] = t34;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] !== t19 || $[79] !== t27 || $[80] !== t35) {
    t36 = <div className={t19}>{t27}{t35}</div>;
    $[78] = t19;
    $[79] = t27;
    $[80] = t35;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== detailMode || $[83] !== example.id || $[84] !== moreDetailsLabel || $[85] !== onOpen) {
    t37 = moreDetailsLabel && <div className="mt-auto flex justify-end border-t border-gray-100 pt-3"><MoreDetailsLink label={moreDetailsLabel} mode={detailMode} contentId={example.id} onOpen={onOpen} /></div>;
    $[82] = detailMode;
    $[83] = example.id;
    $[84] = moreDetailsLabel;
    $[85] = onOpen;
    $[86] = t37;
  } else {
    t37 = $[86];
  }
  let t38;
  if ($[87] !== t12 || $[88] !== t17 || $[89] !== t36 || $[90] !== t37) {
    t38 = <article className={t12}>{t17}{t36}{t37}</article>;
    $[87] = t12;
    $[88] = t17;
    $[89] = t36;
    $[90] = t37;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  return t38;
}

/**
 * Map a content record to the DemandExample shape the ExampleCard renders.
 * Pulls the demand taxonomy off `content.data.demand` and uses the record's
 * title/blurb/image for the post preview.
 */
function _temp2(point) {
  return <li key={point} className="text-sm leading-relaxed text-gray-500"><span className="mr-1.5 text-gray-300">•</span>{point}</li>;
}
export function contentToDemandExample(content: ApiContent): DemandExample {
  return {
    id: content.slug || String(content.id),
    brand: content.title || 'Untitled',
    blurb: content.blurb || undefined,
    whatTheyDid: content.blurb || '',
    whyItWorks: '',
    source: 'Inspiration',
    attributes: getMarketingAttributesFromContent(content),
    imageUrl: content.image || undefined,
    // Only case studies carry a long-form body worth previewing on the card.
    contentPreview: content.type === 'case-study' ? content.content || undefined : undefined,
    isCaseStudy: content.type === 'case-study',
    // Numeric id + follow state for the (optional) follow button. `id` above is
    // the slug used for navigation, so the follow button needs this separately.
    followObjectId: content.id,
    following: content.following,
    // Key metrics surfaced on the detail page; opt-in on the card via
    // `showHighlights`.
    highlights: (content.data as any)?.highlights as Highlight[] || undefined
  };
}